import axios from 'axios';
import store from './store';

// Relative base: requests are same-origin (Vite proxy in dev, nginx in prod),
// so the session cookie is sent automatically.
const BACKEND_HOST = '';
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

class SessionManager {
  constructor() {
    this.isCheckingSession = false;
    this.sessionCheckTimer = null;
    this.setupAxiosInterceptors();
    this.startSessionMonitoring();
  }

  setupAxiosInterceptors() {
    // Response interceptor to handle 401 errors
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          this.handleSessionExpired();
        }
        return Promise.reject(error);
      }
    );
  }

  handleSessionExpired() {
    console.warn('Session expired - logging out user');
    store.dispatch('logout');
    
    // Only redirect if not already on login page
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  async checkSession() {
    if (this.isCheckingSession) {
      return; // Avoid multiple simultaneous checks
    }

    this.isCheckingSession = true;
    
    try {
      const response = await axios.get(BACKEND_HOST + '/api/session');
      
      if (response.data.authenticated) {
        // Session is valid, update store with current data
        const userData = {
          user: response.data.user,
          userId: response.data.user_id,
          role: response.data.role,
          isAdmin: response.data.isAdmin,
          childId: response.data.childId || [],
          isTeacher: response.data.isTeacher,
          isAssistant: response.data.isAssistant,
          isDancer: response.data.isDancer,
          isParent: response.data.isParent,
          test_portal: response.data.test_portal,
        };
        
        if (typeof response.data.test_portal !== 'undefined') {
          store.commit('SET_TEST_PORTAL', response.data.test_portal);
        }

        // Update store
        store.commit('LOGIN', userData);
        
        // Update localStorage
        this.updateLocalStorage(userData);
        
        return true;
      } else {
        // Session is invalid/expired
        this.handleSessionExpired();
        return false;
      }
    } catch (error) {
      console.error('Session check failed:', error);
      this.handleSessionExpired();
      return false;
    } finally {
      this.isCheckingSession = false;
    }
  }

  updateLocalStorage(userData) {
    localStorage.setItem('user', userData.user);
    localStorage.setItem('userId', userData.userId);
    localStorage.setItem('role', userData.role);
    localStorage.setItem('isAdmin', userData.isAdmin);
    localStorage.setItem('isTeacher', userData.isTeacher);
    localStorage.setItem('isAssistant', userData.isAssistant);
    localStorage.setItem('isDancer', userData.isDancer);
    localStorage.setItem('isParent', userData.isParent);
    localStorage.setItem('childId', JSON.stringify(userData.childId || []));
  }

  startSessionMonitoring() {
    // Check session immediately if user is authenticated
    if (store.getters.isAuthenticated) {
      this.checkSession();
    }

    // Set up periodic session checks
    this.sessionCheckTimer = setInterval(() => {
      if (store.getters.isAuthenticated) {
        this.checkSession();
      }
    }, SESSION_CHECK_INTERVAL);
  }

  stopSessionMonitoring() {
    if (this.sessionCheckTimer) {
      clearInterval(this.sessionCheckTimer);
      this.sessionCheckTimer = null;
    }
  }

  // Manual session validation (can be called from components)
  async validateSession() {
    return await this.checkSession();
  }

  // Force logout (can be called from components)
  forceLogout() {
    this.stopSessionMonitoring();
    store.dispatch('logout');
    window.location.href = '/login';
  }
}

// Create and export singleton instance
const sessionManager = new SessionManager();
if (typeof window !== 'undefined') {
  window.sessionManager = sessionManager;
}

export default sessionManager;